import type { PartialEvalCase, PartialEvalRun } from "~/services/evals";

/** The filter options a suite offers, collected from the cases and runs on screen. */
/** Every distinct dataset id attached to the cases on screen. */
export function collectDatasetIds(cases: PartialEvalCase[]): string[] {
  return [...new Set(cases.flatMap((evalCase) => evalCase.dataset_ids))];
}
/** Every distinct agent id the runs on screen evaluated, newest run first. */
export function collectAgentIds(runs: PartialEvalRun[]): string[] {
  return [...new Set(runs.map((run) => run.agent_id).filter((id): id is string => id != null))];
}
/** Every distinct tag across the cases and runs on screen, alphabetised. */
export function collectTags(cases: PartialEvalCase[], runs: PartialEvalRun[]): string[] {
  const tags = new Set<string>();
  for (const evalCase of cases) {
    for (const tag of evalCase.tags) {
      tags.add(tag);
    }
  }
  for (const run of runs) {
    for (const tag of run.tags) {
      tags.add(tag);
    }
  }
  return [...tags].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
}
/**
 * The cases a launch with these tags would run; an empty selection means every
 * case. Containment, not overlap — a case is selected only if it carries every
 * tag asked for, mirroring the Postgres `@>` behind `GET /eval-cases?tags=`.
 */
export function casesSelectedByTags(cases: PartialEvalCase[], tags: string[]): PartialEvalCase[] {
  if (tags.length === 0) {
    return cases;
  }
  return cases.filter((evalCase) => tags.every((tag) => evalCase.tags.includes(tag)));
}
export interface LaunchTagOption {
  tag: string;
  /** Cases that would remain if this tag were added to the current selection. */
  caseCount: number;
  selected: boolean;
}
/**
 * The tags a launch can select by, each carrying what picking it would leave.
 * Counts are conditional on the rest of the selection, so the containment rule is
 * visible in the control: adding a second tag makes every other count fall, and a
 * dead-end combination shows its zero before it is launched. Sourced from the
 * cases, since a tag no case carries can only produce an empty run.
 */
export function launchTagOptions(cases: PartialEvalCase[], selected: string[]): LaunchTagOption[] {
  const tags = new Set<string>();
  for (const evalCase of cases) {
    for (const tag of evalCase.tags) {
      tags.add(tag);
    }
  }
  return [...tags]
    .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }))
    .map((tag) => ({
      tag,
      caseCount: selected.includes(tag)
        ? casesSelectedByTags(cases, selected).length
        : casesSelectedByTags(cases, [...selected, tag]).length,
      selected: selected.includes(tag),
    }));
}
